/**
 * EIR view of assigned founders' investor pipelines. Lists each founder with
 * contact counts and follow-up health; opening one loads that founder's CRM
 * (read-only) and a Notes tab in a slide panel.
 *
 * Auto-opens a founder when navigated here with location.state.openFounderId
 * (e.g. from the EIR dashboard). EIR-only: other roles see a placeholder.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getMyFounders, getFounderCRM } from '../services/apiService';
import SlidePanel from '../components/SlidePanel';
import NotesTab from '../components/NotesTab';

const STAGE_COLORS = {
  prospecting:  { color: '#6b7280', bg: '#f3f4f6' },
  contacted:    { color: '#2563eb', bg: '#eff6ff' },
  interested:   { color: '#d97706', bg: '#fef3c7' },
  term_sheet:   { color: '#7c3aed', bg: '#f5f3ff' },
  committed:    { color: '#059669', bg: '#ecfdf5' },
  closed_won:   { color: '#15803d', bg: '#dcfce7' },
  closed_lost:  { color: '#dc2626', bg: '#fee2e2' },
};

const STAGE_LABELS = {
  prospecting: 'Prospecting', contacted: 'Contacted', interested: 'Interested',
  term_sheet: 'Term Sheet', committed: 'Committed', closed_won: 'Closed Won', closed_lost: 'Closed Lost',
};

// Parse a YYYY-MM-DD string as a local date (avoids the UTC shift that
// new Date('YYYY-MM-DD') would introduce).
function parseLocalDate(str) {
  const [y, m, d] = str.split('-').map(Number);
  return new Date(y, m - 1, d);
}

// Red if the follow-up is overdue, amber if within 3 days, null otherwise.
function followUpColor(dateStr) {
  if (!dateStr) return null;
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const diff = Math.round((parseLocalDate(dateStr) - today) / 86400000);
  if (diff < 0)  return '#dc2626';
  if (diff <= 3) return '#d97706';
  return null;
}

/** EIR dashboard of assigned founders' investor-pipeline health. */
export default function EIRCRM() {
  const { user } = useAuth();
  const location = useLocation();
  const [founders, setFounders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [panelOpen, setPanelOpen] = useState(false);
  const [panelFounder, setPanelFounder] = useState(null);
  const [crmDetail, setCrmDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [panelTab, setPanelTab] = useState('overview');

  useEffect(() => {
    getMyFounders().then(setFounders).finally(() => setLoading(false));
  }, []);

  const openFounder = useCallback((founder) => {
    setPanelFounder(founder);
    setPanelOpen(true);
    setPanelTab('overview');
    setCrmDetail(null);
    setDetailLoading(true);
    getFounderCRM(founder.id).then(setCrmDetail).finally(() => setDetailLoading(false));
  }, []);

  useEffect(() => {
    if (loading || !founders.length) return;
    const id = location.state?.openFounderId;
    if (!id) return;
    const founder = founders.find((f) => f.id === id);
    if (founder) openFounder(founder);
  }, [loading, founders]); // openFounder is stable (useCallback)

  if (user?.role !== 'eir') {
    return (
      <div style={styles.page}>
        <div style={styles.inner}>
          <p style={styles.dim}>This view is for EIRs.</p>
        </div>
      </div>
    );
  }

  const totalContacts = founders.reduce((s, f) => s + (f.crm?.total || 0), 0);
  const totalOverdue = founders.reduce((s, f) => s + (f.crm?.overdue || 0), 0);

  return (
    <div style={styles.page}>
      <div style={styles.inner}>

        <div style={styles.header}>
          <h1 style={styles.title}>Investor Hygiene</h1>
          <p style={styles.subtitle}>Track your founders' investor pipeline activity and follow-up health.</p>
        </div>

        {!loading && founders.length > 0 && (
          <div style={styles.statsRow}>
            <StatCard label="Founders" value={founders.length} />
            <StatCard label="Total Contacts" value={totalContacts} />
            <StatCard label="Overdue Follow-ups" value={totalOverdue} alert={totalOverdue > 0} />
          </div>
        )}

        {loading && <p style={styles.dim}>Loading…</p>}
        {!loading && founders.length === 0 && <p style={styles.dim}>No founders assigned yet.</p>}

        {!loading && founders.length > 0 && (
          <div style={styles.tableWrap}>
            <table style={styles.table}>
              <thead>
                <tr style={styles.thead}>
                  <th style={styles.th}>Founder</th>
                  <th style={styles.th}>Company</th>
                  <th style={styles.th}>Contacts</th>
                  <th style={styles.th}>Overdue</th>
                  <th style={styles.th}>Last Activity</th>
                </tr>
              </thead>
              <tbody>
                {founders.map((f) => {
                  const crm = f.crm || { total: 0, overdue: 0, last_activity: null };
                  return (
                    <tr key={f.id} style={styles.tr} onClick={() => openFounder(f)}>
                      <td style={{ ...styles.td, fontWeight: 700 }}>
                        {f.first_name} {f.last_name}
                      </td>
                      <td style={styles.td}>{f.company_name || <span style={styles.dim}>—</span>}</td>
                      <td style={styles.td}>
                        <span style={styles.countBadge}>{crm.total}</span>
                      </td>
                      <td style={styles.td}>
                        {crm.overdue > 0
                          ? <span style={styles.overdueBadge}>{crm.overdue} overdue</span>
                          : <span style={styles.okBadge}>Up to date</span>}
                      </td>
                      <td style={styles.td}>
                        {crm.last_activity
                          ? parseLocalDate(crm.last_activity).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
                          : <span style={styles.dim}>No activity</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

      </div>

      <SlidePanel
        open={panelOpen}
        onClose={() => setPanelOpen(false)}
        title={panelFounder ? `${panelFounder.first_name} ${panelFounder.last_name}` : ''}
      >
        <div style={tabStyles.tabBar}>
          {['overview', 'notes'].map((t) => (
            <button key={t} style={panelTab === t ? tabStyles.tabActive : tabStyles.tab} onClick={() => setPanelTab(t)}>
              {t === 'overview' ? 'Overview' : 'Notes'}
            </button>
          ))}
        </div>
        {panelTab === 'overview' ? (
          <>
            {detailLoading && <p style={styles.dim}>Loading contacts…</p>}
            {!detailLoading && crmDetail && <CRMDetail data={crmDetail} />}
          </>
        ) : (
          panelFounder && (
            <NotesTab
              founderUserId={panelFounder.id}
              founderName={`${panelFounder.first_name} ${panelFounder.last_name}`}
              page="crm"
            />
          )
        )}
      </SlidePanel>
    </div>
  );
}

function CRMDetail({ data }) {
  if (data.contacts.length === 0) {
    return <p style={styles.dim}>{data.founder.first_name} has no investor contacts yet.</p>;
  }

  const overdue = data.contacts.filter((c) => c.overdue);
  const active  = data.contacts.filter((c) => !c.overdue);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-5)' }}>
      <div style={{ display: 'flex', gap: 12 }}>
        <div style={detailStyles.stat}>
          <p style={detailStyles.statNum}>{data.contacts.length}</p>
          <p style={detailStyles.statLabel}>Total</p>
        </div>
        <div style={{ ...detailStyles.stat, ...(overdue.length > 0 ? { background: '#fee2e2' } : {}) }}>
          <p style={{ ...detailStyles.statNum, ...(overdue.length > 0 ? { color: '#dc2626' } : {}) }}>{overdue.length}</p>
          <p style={detailStyles.statLabel}>Overdue</p>
        </div>
      </div>

      {overdue.length > 0 && (
        <div>
          <p style={detailStyles.sectionLabel}>Overdue Follow-ups</p>
          {overdue.map((c) => <ContactLine key={c.id} contact={c} />)}
        </div>
      )}

      <div>
        <p style={detailStyles.sectionLabel}>All Contacts</p>
        {data.contacts.map((c) => <ContactLine key={c.id} contact={c} />)}
      </div>
    </div>
  );
}

function ContactLine({ contact }) {
  const stage = STAGE_COLORS[contact.stage] || STAGE_COLORS.prospecting;
  const fuColor = followUpColor(contact.next_follow_up_date);
  return (
    <div style={detailStyles.contactRow}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <p style={detailStyles.contactName}>{contact.name}</p>
        {contact.organization && <p style={detailStyles.contactOrg}>{contact.organization}</p>}
      </div>
      <span style={{ ...detailStyles.stagePill, background: stage.bg, color: stage.color }}>
        {STAGE_LABELS[contact.stage] || contact.stage}
      </span>
      {contact.next_follow_up_date && (
        <span style={{ ...detailStyles.dateLine, ...(fuColor ? { color: fuColor, fontWeight: 700 } : {}) }}>
          {parseLocalDate(contact.next_follow_up_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
        </span>
      )}
    </div>
  );
}

function StatCard({ label, value, alert }) {
  return (
    <div style={{ ...styles.statCard, ...(alert ? { background: '#fee2e2' } : {}) }}>
      <p style={{ ...styles.statValue, ...(alert ? { color: '#dc2626' } : {}) }}>{value}</p>
      <p style={styles.statLabel}>{label}</p>
    </div>
  );
}

const styles = {
  page: { flex: 1, overflowY: 'auto', background: 'linear-gradient(to bottom, var(--color-frost) 0%, var(--color-mint-dark) 100%) fixed', padding: 'var(--space-8)' },
  inner: { maxWidth: "98%", margin: "0 auto" },
  header: { marginBottom: 'var(--space-6)', paddingTop: 'var(--space-4)' },
  title: { fontFamily: 'var(--font-accent)', fontSize: 64, fontWeight: 500, color: 'var(--color-rust)', lineHeight: 1.1, marginBottom: 'var(--space-2)' },
  subtitle: { fontFamily: 'var(--font-body)', fontSize: 22, fontWeight: 'var(--font-weight-page-subtitle)', color: 'var(--color-forest)', opacity: 0.6, lineHeight: 1.5 },
  dim: { fontFamily: 'var(--font-body)', fontSize: 15, color: 'var(--color-forest)', opacity: 0.45, fontStyle: 'italic' },
  statsRow: { display: 'flex', gap: 'var(--space-4)', marginBottom: 'var(--space-6)', flexWrap: 'wrap', maxWidth: 800 },
  statCard: { background: 'var(--color-frost)', borderRadius: 'var(--radius-md)', padding: '16px 24px', boxShadow: 'var(--shadow-card)', flex: 1, textAlign: 'center' },
  statValue: { fontFamily: 'var(--font-accent)', fontSize: 28, fontWeight: 800, color: 'var(--color-forest)', lineHeight: 1.1, marginBottom: 4 },
  statLabel: { fontFamily: 'var(--font-body)', fontSize: 15, fontWeight: 700, color: 'var(--color-forest)', opacity: 0.5, textTransform: 'uppercase', letterSpacing: '0.06em' },
  tableWrap: { background: 'var(--color-frost)', borderRadius: 'var(--radius-lg)', boxShadow: 'var(--shadow-card)', overflowX: 'auto', WebkitOverflowScrolling: 'touch' },
  table: { width: '100%', minWidth: 560, borderCollapse: 'collapse' },
  thead: { background: 'var(--color-mint-dark)' },
  th: { fontFamily: 'var(--font-body)', fontSize: 15, fontWeight: 700, color: 'var(--color-forest)', opacity: 0.55, textTransform: 'uppercase', letterSpacing: '0.06em', padding: '10px 20px', textAlign: 'left' },
  tr: { borderBottom: '1px solid var(--color-mint)', cursor: 'pointer' },
  td: { fontFamily: 'var(--font-body)', fontSize: 15, color: 'var(--color-forest)', padding: '14px 20px', verticalAlign: 'middle' },
  countBadge: { display: 'inline-block', padding: '2px 10px', borderRadius: 'var(--radius-full)', background: 'var(--color-mint)', fontWeight: 700, fontSize: 15 },
  overdueBadge: { display: 'inline-block', padding: '2px 10px', borderRadius: 'var(--radius-full)', background: '#fee2e2', color: '#dc2626', fontWeight: 700, fontSize: 15 },
  okBadge: { display: 'inline-block', padding: '2px 10px', borderRadius: 'var(--radius-full)', background: '#dcfce7', color: '#15803d', fontWeight: 700, fontSize: 15 },
};

const tabStyles = {
  tabBar:    { display: 'flex', gap: 4, marginBottom: 16 },
  tab:       { fontFamily: 'var(--font-body)', fontSize: 13, fontWeight: 600, padding: '6px 16px', borderRadius: 'var(--radius-sm)', border: '1.5px solid var(--color-mint-dark)', background: 'var(--color-frost)', color: 'var(--color-forest)', cursor: 'pointer' },
  tabActive: { fontFamily: 'var(--font-body)', fontSize: 13, fontWeight: 700, padding: '6px 16px', borderRadius: 'var(--radius-sm)', border: '1.5px solid var(--color-forest)', background: 'var(--color-forest)', color: '#fff', cursor: 'pointer' },
};

const detailStyles = {
  stat: { flex: 1, background: 'var(--color-mint)', borderRadius: 'var(--radius-md)', padding: '14px 16px', textAlign: 'center' },
  statNum: { fontFamily: 'var(--font-accent)', fontSize: 24, fontWeight: 800, color: 'var(--color-forest)', lineHeight: 1.1, marginBottom: 2 },
  statLabel: { fontFamily: 'var(--font-body)', fontSize: 15, fontWeight: 700, color: 'var(--color-forest)', opacity: 0.5, textTransform: 'uppercase', letterSpacing: '0.05em' },
  sectionLabel: { fontFamily: 'var(--font-body)', fontSize: 15, fontWeight: 700, color: 'var(--color-forest)', opacity: 0.5, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 },
  contactRow: { display: 'flex', alignItems: 'center', gap: 10, padding: '9px 0', borderBottom: '1px solid #f0f0f0' },
  contactName: { fontFamily: 'var(--font-body)', fontSize: 15, fontWeight: 600, color: 'var(--color-forest)' },
  contactOrg: { fontFamily: 'var(--font-body)', fontSize: 15, color: 'var(--color-forest)', opacity: 0.55, marginTop: 1 },
  stagePill: { padding: '2px 8px', borderRadius: 'var(--radius-full)', fontSize: 10, fontWeight: 700, flexShrink: 0 },
  dateLine: { fontFamily: 'var(--font-body)', fontSize: 15, color: 'var(--color-forest)', opacity: 0.6, flexShrink: 0 },
};
